/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Field, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Button, Form as NXUIForm } from '@nexigendigital/nxui'
import { useAppSelector } from 'store/hooks'
import { useLoginMutation } from 'api/app'

/**********************************************************************************************************
 *   STYLE IMPORTS
 **********************************************************************************************************/
import { Login } from './form.style'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function LoginForm() {
    const loginRef = useRef<FormikProps<{ email: string, password: string, remember: boolean }>>(null)
    const navigate = useNavigate()
    const { appSession: { account, two_factor_required } } = useAppSelector((state) => state.app)
    const [login, { isSuccess: loginSuccess, isError: loginError, data: loginData }] = useLoginMutation()

    function redirectToForgot() {
        navigate('/login/forgot', { replace: true })
    }

    useEffect(() => {
        if (loginSuccess && two_factor_required) {
            navigate('/login/two-factor')
        } else if (loginSuccess && !two_factor_required && !account) {
            navigate('/login/accounts')
        }

        // Error handling is done here -- for success handling, look at parent Login component.
        if (((loginSuccess && loginData?.data?.error) || loginError) && loginRef.current) {
            loginRef.current.setSubmitting(false)
            loginRef.current.resetForm()
        }
    }, [two_factor_required, account, loginSuccess, loginData, loginError])

    return (
        <Formik
            innerRef={loginRef}
            initialValues={{
                email: '',
                password: '',
                remember: false
            }}
            validationSchema={Yup.object({
                email: Yup.string().email('Invalid email address').required('Required'),
                password: Yup.string().required('Required')
            })}
            onSubmit={({ email, password, remember }) => {
                login({ email, password, remember })
            }}
        >
            {({ isSubmitting }) => {
                return (
                    <Login.Form>
                        <Login.Body>
                            <Field
                                id={'email'}
                                label={'Email address'}
                                name={'email'}
                                type={'text'}
                                disabled={isSubmitting}
                                autoComplete={'username'}
                                component={NXUIForm.InputField}
                            />
                            <Field
                                id={'password'}
                                label={'Password'}
                                name={'password'}
                                type={'password'}
                                disabled={isSubmitting}
                                autoComplete={'current-password'}
                                component={NXUIForm.InputField}
                            />
                            <Login.Checkbox>
                                <Field
                                    id={'remember'}
                                    label={'Remember me'}
                                    name={'remember'}
                                    type={'checkbox'}
                                    disabled={isSubmitting}
                                    component={NXUIForm.CheckboxField}
                                />
                            </Login.Checkbox>
                        </Login.Body>
                        <Login.Footer>
                            <Anchor color={'primary'} onClick={redirectToForgot}>
                                Forgot my password
                            </Anchor>
                            <Button
                                color={'primary'}
                                type={'submit'}
                                disabled={isSubmitting}
                                loading={isSubmitting ? 'Logging in' : false}
                            >
                                Login
                            </Button>
                        </Login.Footer>
                    </Login.Form>
                )
            }}
        </Formik>
    )
}
